import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useMetaApiInstance } from '../api';
import { HOST_QUERY_KEY } from '../constants';

import type { AxiosError } from 'axios';
import type { UseMutationOptions } from '@tanstack/react-query';
import type { FavoriteService } from '../constants/types';

type EditFavoriteServicesOptions = UseMutationOptions<
  FavoriteService[],
  AxiosError,
  FavoriteService[],
  FavoriteService[]
>;

/**
 * 즐겨찾는 서비스를 변경하는 훅입니다.
 * @description
 * - react-query로 mutate를 제공합니다.
 */
const useEditFavoriteService = (options?: EditFavoriteServicesOptions) => {
  const client = useQueryClient();
  const { editFavoriteService } = useMetaApiInstance();

  const mutation = async (favorites: FavoriteService[]) => {
    const param = { serviceKeywords: favorites.map(service => service.keyword) };
    const response = await editFavoriteService(param);

    return response;
  };

  return useMutation({
    mutationKey: [HOST_QUERY_KEY.EDIT_FAVORITE_SERVICE],
    mutationFn: mutation,
    onMutate: async newFavorites => {
      await client.cancelQueries({ queryKey: [HOST_QUERY_KEY.GET_FAVORITE_SERVICE] });

      const previousFavorites =
        client.getQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE]) || [];

      client.setQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], newFavorites);

      return previousFavorites;
    },
    onSuccess: res => {
      const newFavorites = res || [];
      client.setQueryData<FavoriteService[]>([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], newFavorites);
    },
    onError: (error, newFavorite, previousFavorites) => {
      if (previousFavorites) {
        client.setQueryData([HOST_QUERY_KEY.GET_FAVORITE_SERVICE], previousFavorites);
      }
    },
    ...options,
  });
};

export default useEditFavoriteService;
